import { definedMap, sleep } from "@glideapps/ts-necessities";
import type { AppDescription, SerializedApp } from "common/components/SerializedApp";
import type { TableName } from "common/database-strings";
import type {
    ComponentDescription,
    ContainerComponentDescription,
    ScreenDescription,
    TabDescription,
    TableColumn,
    TypeSchema,
} from "common/description";
import { findTable, getScreenComponents, isClassOrArrayScreenDescription } from "common/description";
import type { CompoundActionNode } from "common/generator/actions/compound";
import type { SourceMetadata } from "common/source-metadata";
import { assertNever, defined, replaceArrayItem } from "common/Support";
import type { RewritingComponentConfiguratorContext } from "generator/components";
import { handlerForComponentKind } from "generator/handlers";
import { getSubComponents, getTablesForComponentInScreen } from "generator/sub-components";
import { getAppEnvironmentForAppID } from "sharedUI/app-state-context";
import type { MaterialGlideAppState } from "webapp/chrome/smart/material-glide-app/material-glide-app";
import type { AppNavigationModel } from "webapp/lib/navigation-model";
import type { UndoAppModificationPayload } from "../lib/undo";
import type { AppAndState, SourcePadState, TemplateStoreInfoForTemplate } from "../store/types";
import { getCurrentAppAndStateOrDefaults } from "../store/types";
import type { GlideAsyncThunk, GlideThunk } from "./action-thunk";

export enum ActionType {
    EnsureTabVisible = "ENSURE_TAB_VISIBLE",
    WriteNavState = "WRITE_NAV_STATE",
    SetSourcePad = "SET_SOURCE_PAD",
    UpdateSerializedApp = "UPDATE_SERIALIZED_APP",
    SetApp = "SET_APP",
    UpdateApp = "UPDATE_APP",
    SetSaveApp = "SET_SAVE_APP",
    SetAppLoading = "SET_APP_LOADING",
    UnloadApps = "UNLOAD_APPS",
    UpdateGlideAppState = "UPDATE_GLIDE_APP_STATE",
    MutateScreenDescription = "MUTATE_SCREEN_DESCRIPTION",
    UpdateTabDescription = "UPDATE_TAB_DESCRIPTION",
    ReorderTabs = "REORDER_TABS",
    AddTab = "ADD_TAB",
    RemoveTab = "REMOVE_TAB",
    SetNewComponents = "SET_NEW_COMPONENTS",
    ModifyTableColumns = "MODIFY_TABLE_COLUMNS",
    SetSchema = "SET_SCHEMA",
    SetOldCurrentTab = "SET_OLD_CURRENT_TAB",
    PushUndoState = "PUSH_UNDO_STATE",
    ApplyAppModification = "UNDO_APP_MODIFICATION",
    SetTemplateInfo = "SET_TEMPLATE_INFO",
    AddSourceMetadata = "ADD_SOURCE_METADATA",
    RemoveNativeTableSourceMetadata = "REMOVE_NATIVE_TABLE_SOURCE_METADATA",
    AddNativeTableSourcePad = "ADD_NATIVE_TABLE_SOURCEPAD",
    RemoveNativeTableSourcePad = "REMOVE_NATIVE_TABLE_SOURCEPAD",
    SetBuilderAction = "SET_BUILDER_ACTION",
    SetReadOnlyApp = "SET_READ_ONLY_APP",
}

export interface AppIDPayload {
    readonly appID: string;
}

interface AppIDWithDataPayload<T> extends AppIDPayload {
    data: T;
}

// FIXME: With `createAction` we shouldn't need this type.
export interface Action<T, P> {
    type: T;
    payload: P;
}

function makeAppIDWithDataAction<T>(actionType: ActionType) {
    return function makeAction(appID: string, data: T): Action<ActionType, AppIDWithDataPayload<T>> {
        return { type: actionType, payload: { appID, data } };
    };
}

export type SetSourcePadPayload = AppIDWithDataPayload<SourcePadState>;
export const setSourcePad = makeAppIDWithDataAction<SourcePadState>(ActionType.SetSourcePad);

export type UpdateSerializedAppPayload = AppIDWithDataPayload<Partial<SerializedApp>>;
export const updateSerializedApp = makeAppIDWithDataAction<Partial<SerializedApp>>(ActionType.UpdateSerializedApp);

export type UpdateGlideAppStatePayload = AppIDWithDataPayload<
    (current: MaterialGlideAppState) => MaterialGlideAppState
>;
export const updateGlideAppState = makeAppIDWithDataAction<(current: MaterialGlideAppState) => MaterialGlideAppState>(
    ActionType.UpdateGlideAppState
);

export type SetAppLoading = AppIDPayload;
export function setAppLoading(appID: string): Action<ActionType, SetAppLoading> {
    return { type: ActionType.SetAppLoading, payload: { appID } };
}

export type SetSaveAppPayload = AppIDPayload;
export function setSaveApp(appID: string): Action<ActionType, SetSaveAppPayload> {
    return { type: ActionType.SetSaveApp, payload: { appID } };
}

export function unloadApps(appIDs: string[]): Action<ActionType, string[]> {
    return { type: ActionType.UnloadApps, payload: appIDs };
}

export type SetAppPayload = AppIDWithDataPayload<AppAndState>;
export const setApp = makeAppIDWithDataAction<AppAndState>(ActionType.SetApp);

export type SetReadOnlyAppPayload = AppIDWithDataPayload<AppDescription>;
export const setReadOnlyApp = makeAppIDWithDataAction<AppDescription>(ActionType.SetReadOnlyApp);

interface UpdateAppPayloadData {
    updates: Partial<AppAndState>;
    didModify: boolean;
}
export type UpdateAppPayload = AppIDWithDataPayload<UpdateAppPayloadData>;
export const updateApp = makeAppIDWithDataAction<UpdateAppPayloadData>(ActionType.UpdateApp);

export interface MutateScreenDescriptionPayload extends AppIDPayload {
    readonly mutator: (
        old: ScreenDescription | undefined,
        ccc: RewritingComponentConfiguratorContext
    ) => ScreenDescription | undefined;
    readonly screenName: string;
}

export function mutateScreenDescription(
    appID: string,
    screenName: string,
    mutator: (
        old: ScreenDescription | undefined,
        ccc: RewritingComponentConfiguratorContext
    ) => ScreenDescription | undefined
): Action<ActionType, MutateScreenDescriptionPayload> {
    const payload: MutateScreenDescriptionPayload = { appID, mutator, screenName };
    return { type: ActionType.MutateScreenDescription, payload };
}

export function updateScreenDescription(
    appID: string,
    updates: Partial<ScreenDescription> | false,
    screenName: string,
    allowNewScreen?: boolean
): GlideThunk {
    return dispatch => {
        dispatch(
            mutateScreenDescription(appID, screenName, old => {
                if (updates === false) return undefined;
                if (old === undefined) {
                    if (allowNewScreen !== true) return undefined;
                    return updates as ScreenDescription;
                }
                return { ...old, ...updates } as ScreenDescription;
            })
        );
    };
}

export interface UpdateTabDescriptionPayload extends AppIDPayload {
    index: number;
    desc: TabDescription;
    forceRebuild: boolean;
}

export function updateTabDescription(
    appID: string,
    index: number,
    desc: TabDescription,
    forceRebuild: boolean
): Action<ActionType, UpdateTabDescriptionPayload> {
    return { type: ActionType.UpdateTabDescription, payload: { appID, index, desc, forceRebuild } };
}

export function updateComponentDescription(
    appID: string,
    screenName: string,
    desc: ComponentDescription,
    updates: Partial<ComponentDescription>
): GlideThunk {
    return dispatch => {
        dispatch(
            mutateScreenDescription(appID, screenName, (old, ccc) => {
                if (old === undefined || !isClassOrArrayScreenDescription(old)) return old;

                const maybeTables = getTablesForComponentInScreen(screenName, old, desc, ccc);
                if (maybeTables === undefined) return old;

                const [componentTables, componentMutatingScreenKind, indexes] = maybeTables;

                const handler = handlerForComponentKind(desc.kind);
                if (handler === undefined) return old;

                const newComponent = handler.updateComponent(
                    desc,
                    updates,
                    componentTables,
                    ccc,
                    componentMutatingScreenKind
                );

                const components = getScreenComponents(old);

                if (indexes.length === 1) {
                    const newComponents = replaceArrayItem(components, indexes[0], newComponent);
                    return { ...old, components: newComponents };
                } else if (indexes.length === 2) {
                    const parent = defined(components[indexes[0]]) as ContainerComponentDescription;
                    const subComponents = defined(getSubComponents(parent));
                    const newSubComponents = replaceArrayItem(subComponents, indexes[1], newComponent);
                    const newParent = { ...parent, components: newSubComponents };
                    const newComponents = replaceArrayItem(components, indexes[0], newParent);
                    return { ...old, components: newComponents };
                } else {
                    return assertNever(indexes);
                }
            })
        );
    };
}

export type ReorderTabsPayload = AppIDWithDataPayload<readonly number[]>;
export const reorderTabs = makeAppIDWithDataAction<readonly number[]>(ActionType.ReorderTabs);

export interface AddTabPayload extends AppIDPayload {
    readonly tabDescription: TabDescription;
    readonly index: number;
}

export function addTab(
    appID: string,
    tabDescription: TabDescription,
    index: number
): Action<ActionType, AddTabPayload> {
    return {
        type: ActionType.AddTab,
        payload: { appID, tabDescription, index },
    };
}

export type RemoveTabPayload = AppIDWithDataPayload<number>;
export const removeTab = makeAppIDWithDataAction<number>(ActionType.RemoveTab);

export type SetOldCurrentTabPayload = AppIDWithDataPayload<number>;
export const setOldCurrentTab = makeAppIDWithDataAction<number>(ActionType.SetOldCurrentTab);

export type WriteNavStatePayload = AppIDWithDataPayload<AppNavigationModel>;
export const writeNavState = makeAppIDWithDataAction<AppNavigationModel>(ActionType.WriteNavState);

export type EnsureTabVisiblePayload = AppIDPayload;
export function ensureTabVisible(appID: string): Action<ActionType, EnsureTabVisiblePayload> {
    return { type: ActionType.EnsureTabVisible, payload: { appID } };
}

export interface ModifyTableColumnsPayload extends AppIDPayload {
    removed: ReadonlyMap<string, readonly string[]>;
    modified: ReadonlyMap<string, readonly TableColumn[]>;
    moved: ReadonlyMap<string, readonly [string, number][]>;
    setProtected: ReadonlyMap<string, readonly [string, boolean][]>;
    rowIDColumns: ReadonlyMap<string, string | undefined>;
}

export function modifyTableColumns(
    appID: string,
    removed: ReadonlyMap<string, readonly string[]>,
    modified: ReadonlyMap<string, readonly TableColumn[]>,
    moved: ReadonlyMap<string, readonly [string, number][]>,
    setProtected: ReadonlyMap<string, readonly [string, boolean][]>,
    rowIDColumns: ReadonlyMap<string, string | undefined>
): Action<ActionType, ModifyTableColumnsPayload> {
    return {
        type: ActionType.ModifyTableColumns,
        payload: { appID, removed, modified, moved, setProtected, rowIDColumns },
    };
}

export interface SetSchemaPayload extends AppIDPayload {
    readonly schema: TypeSchema;
}

export function setSchema(appID: string, schema: TypeSchema): Action<ActionType, SetSchemaPayload> {
    return { type: ActionType.SetSchema, payload: { appID, schema } };
}

export interface ApplyAppModificationPayload extends AppIDPayload {
    readonly appModification: UndoAppModificationPayload;
    readonly isRedo: boolean;
}

export function applyAppModification(
    appModification: UndoAppModificationPayload,
    isRedo: boolean
): Action<ActionType, ApplyAppModificationPayload> {
    return {
        type: ActionType.ApplyAppModification,
        payload: { appID: appModification.appID, appModification, isRedo },
    };
}

export type PushUndoStatePayload = AppIDPayload;
export function pushUndoState(appID: string): Action<ActionType, PushUndoStatePayload> {
    return { type: ActionType.PushUndoState, payload: { appID } };
}

export type SetTemplateStorePayload = AppIDPayload & {
    storeInfo?: TemplateStoreInfoForTemplate;
};
export function setTemplateStore(
    appID: string,
    storeInfo: TemplateStoreInfoForTemplate | undefined
): Action<ActionType, SetTemplateStorePayload> {
    return { type: ActionType.SetTemplateInfo, payload: { appID, storeInfo } };
}

export interface AddSourceMetadataPayload extends AppIDPayload {
    readonly sourceMetadata: SourceMetadata;
}
export function addSourceMetadata(
    appID: string,
    sourceMetadata: SourceMetadata
): Action<ActionType, AddSourceMetadataPayload> {
    return { type: ActionType.AddSourceMetadata, payload: { appID, sourceMetadata } };
}

// FIXME: This is gross.
// We only have to do this because we mutate the schema separate from the
// actual published app.
export function waitForTableThenAddSourceMetadata(
    appID: string,
    tableName: TableName,
    sourceMetadata: SourceMetadata
): GlideAsyncThunk {
    return async (dispatch, getStore) => {
        while (true) {
            const currentAppAndState = getCurrentAppAndStateOrDefaults(appID, getStore().apps);
            if (definedMap(currentAppAndState.sourcePad?.schema, s => findTable(s, tableName)) !== undefined) break;

            // FIXME: Surely there's a better way to do this?
            await sleep(250);
        }
        dispatch(addSourceMetadata(appID, sourceMetadata));
        dispatch(addNativeTableSourcePad(appID, sourceMetadata));

        // FIXME: This is a gross hack to fix a bug that results from the
        // gross hack above.  The schema gets updated before the source
        // metadata, but the computation model now also depends on the source
        // metadata to resolve native table computed columns, so we need to
        // rebuilt it when the source metadata changes.  Ugh.
        getAppEnvironmentForAppID(appID)?.dataStore.appEnvironmentUpdated();
    };
}

export interface RemoveSourceMetadataForNativeTablePayload extends AppIDPayload {
    readonly nativeTableID: string;
}

export function removeNativeTableSourceMetadata(
    appID: string,
    nativeTableID: string
): Action<ActionType, RemoveSourceMetadataForNativeTablePayload> {
    return { type: ActionType.RemoveNativeTableSourceMetadata, payload: { appID, nativeTableID } };
}

export interface RemoveNativeTableSourcePadPayload extends AppIDPayload {
    readonly nativeTableID: string;
}

export function removeNativeTableSourcePad(
    appID: string,
    nativeTableID: string
): Action<ActionType, RemoveNativeTableSourcePadPayload> {
    return { type: ActionType.RemoveNativeTableSourcePad, payload: { appID, nativeTableID } };
}

export interface AddNativeTableSourcePadPayload extends AppIDPayload {
    readonly sourceMetadata: SourceMetadata;
}

export function addNativeTableSourcePad(
    appID: string,
    sourceMetadata: SourceMetadata
): Action<ActionType, AddNativeTableSourcePadPayload> {
    return { type: ActionType.AddNativeTableSourcePad, payload: { appID, sourceMetadata } };
}

export interface SetBuilderActionPayload extends AppIDPayload {
    readonly actionID: string;
    readonly tableName: TableName;
    readonly title: string | undefined;
    readonly description: string | undefined;
    readonly action: CompoundActionNode;
}

export function setBuilderAction(
    appID: string,
    actionID: string,
    tableName: TableName,
    title: string | undefined,
    description: string | undefined,
    action: CompoundActionNode
): Action<ActionType, SetBuilderActionPayload> {
    return { type: ActionType.SetBuilderAction, payload: { appID, actionID, tableName, title, description, action } };
}
